import { SoundButton } from "../../../../../common/SoundButton/SoundButton";
import { useTranslation } from "react-i18next";
import coralFrame from "../../../../../../assets/static/images/slot/spin-button-v2/spin-coral-frame.webp";
import betFace from "../../../../../../assets/static/images/slot/spin-button-v2/bet-multiplier-btn.webp";
import maxBetFace from "../../../../../../assets/static/images/slot/spin-button-v2/max-bet-multiplier-btn.webp";
import idleFace from "../../../../../../assets/static/images/slot/spin-button-v2/spin-btn-face-idle.webp";
import activeFace from "../../../../../../assets/static/images/slot/spin-button-v2/spin-btn-face-active.webp";
import { useSpinButtonPress } from "../../../../../../logic/hooks/useSpinButtonPress";
import { useFitContentWidth } from "../../../../../../logic/hooks/useFitContentWidth";
import "./SpinButton.scss";

interface SpinButtonProps {
    disabled: boolean;
    isBusy: boolean;
    isAutoSpinning: boolean;
    canStartAutospin: boolean;
    betMultiplier: number;
    isMaxBet?: boolean;
    betDisabled?: boolean;
    spinLabel?: string;
    onSpin: () => void;
    onStartAutospin: () => void;
    onStopAutospin: () => void;
    onBetChange?: () => void;
}

export function SpinButton({
    disabled,
    isBusy,
    isAutoSpinning,
    canStartAutospin,
    betMultiplier,
    isMaxBet = false,
    betDisabled = disabled || isBusy || isAutoSpinning,
    spinLabel,
    onSpin,
    onStartAutospin,
    onStopAutospin,
    onBetChange,
}: SpinButtonProps) {
    const { t, i18n } = useTranslation();
    const multiplierLabel = t("slotMachine.controls.multiplier", {
        multiplier: new Intl.NumberFormat(i18n.resolvedLanguage ?? "en", {
            notation: "compact",
            maximumFractionDigits: 1,
        }).format(betMultiplier),
    });
    const betFit = useFitContentWidth(multiplierLabel);
    const { isPressed, ...pressHandlers } = useSpinButtonPress({
        disabled,
        isBusy,
        canStartAutospin,
        isAutoSpinning,
        onSpin,
        onStartAutospin,
        onStopAutospin,
    });
    const state = isAutoSpinning
        ? "autospin"
        : isPressed || isBusy
          ? "pressed"
          : "idle";
    const label = t(
        isAutoSpinning ? "slotMachine.controls.stop" : "slotMachine.spin",
        { lng: "en" },
    );
    const hint = t(
        isAutoSpinning
            ? "slotMachine.controls.autospin"
            : "slotMachine.controls.holdForAuto",
    );
    const labelFit = useFitContentWidth(label);
    const hintFit = useFitContentWidth(hint);

    return (
        <div className="spin-button" data-spin-state={state}>
            <img
                className="spin-button__frame"
                src={coralFrame}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
            <SoundButton
                clickSound="none"
                {...pressHandlers}
                type="button"
                className="spin-button__spin"
                disabled={!isAutoSpinning && disabled}
                aria-disabled={!isAutoSpinning && isBusy}
                aria-label={
                    isAutoSpinning
                        ? t("slotMachine.controls.stopAutospin")
                        : (spinLabel ?? t("slotMachine.controls.spinLabel"))
                }
                aria-pressed={isAutoSpinning}
                onContextMenu={(event) => event.preventDefault()}
            >
                <img
                    className="spin-button__face spin-button__face--idle"
                    src={idleFace}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
                <img
                    className="spin-button__face spin-button__face--active"
                    src={activeFace}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
                <span className="spin-button__outline" aria-hidden="true" />
                <span
                    className="spin-button__label-container"
                    lang="en"
                    dir="ltr"
                    ref={labelFit.containerRef}
                    aria-hidden="true"
                >
                    <span
                        className="spin-button__label"
                        data-text={label}
                        ref={labelFit.contentRef}
                    >
                        {label}
                    </span>
                </span>
                <span
                    className="spin-button__hint-container"
                    ref={hintFit.containerRef}
                    aria-hidden="true"
                >
                    <span
                        className="spin-button__hint"
                        ref={hintFit.contentRef}
                    >
                        {hint}
                    </span>
                </span>
            </SoundButton>
            <SoundButton
                clickSound="none"
                type="button"
                className={["spin-button__bet", isMaxBet && "is-max"]
                    .filter(Boolean)
                    .join(" ")}
                onClick={onBetChange}
                disabled={!onBetChange || betDisabled}
                aria-label={t(
                    onBetChange
                        ? "slotMachine.controls.changeBet"
                        : "slotMachine.controls.currentBet",
                    { multiplier: betMultiplier },
                )}
            >
                <img
                    className="spin-button__bet-face"
                    src={betFace}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
                <img
                    className="spin-button__bet-face spin-button__bet-face--max"
                    src={maxBetFace}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
                <span
                    className="spin-button__bet-label"
                    ref={betFit.containerRef}
                    dir="ltr"
                    aria-hidden="true"
                >
                    <span ref={betFit.contentRef}>{multiplierLabel}</span>
                </span>
                {isMaxBet && (
                    <span className="spin-button__bet-max">
                        {t("slotMachine.controls.maxBet")}
                    </span>
                )}
            </SoundButton>
        </div>
    );
}
